<template>
  <div class="sales-dashboard min-h-screen">
    <!-- Header -->
    <div class="sd-header">
      <div class="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="sd-header__logo">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-xl font-semibold text-secondary-800">Pipeline Dashboard</h1>
              <p class="text-sm text-gray-500">Affärsmöjligheter och försäljningsanalys</p>
            </div>
          </div>
          <div class="flex items-center gap-3">
            <!-- Calle Baumgarten Filter -->
            <button
              class="flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium cursor-pointer transition-colors"
              :class="excludeCalleBaumgarten ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50 border-gray-200 text-gray-700'"
              @click="excludeCalleBaumgarten = !excludeCalleBaumgarten"
            >
              <BaseIcon :icon="excludeCalleBaumgarten ? 'user-slash' : 'user'" :class="excludeCalleBaumgarten ? 'text-red-600' : 'text-gray-500'" />
              {{ excludeCalleBaumgarten ? 'Calle exkluderad' : 'Inkl. Calle' }}
            </button>
            <div class="flex items-center gap-2">
              <button v-if="selectedPipelineUpload" class="sd-btn sd-btn--secondary" @click="handleExportPipeline">
                <BaseIcon icon="download" class="mr-1" />
                Exportera pipeline
              </button>
              <button v-if="selectedWonDealsUpload" class="sd-btn sd-btn--secondary" @click="handleExportWonDeals">
                <BaseIcon icon="download" class="mr-1" />
                Exportera vunna affärer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <main class="w-full px-4 sm:px-6 lg:px-8 py-6">
      <!-- Dashboard Tabs -->
      <div>
        <div class="mb-6">
          <nav class="inline-flex w-full bg-gray-100 rounded-xl p-1 overflow-x-auto" aria-label="Tabs">
            <button v-for="tab in tabs" :key="tab.id"
              class="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-all"
              :class="activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'"
              @click="activeTab = tab.id">
              <BaseIcon :icon="tab.icon" class="mr-2 fa-fw" />
              {{ tab.label }}
            </button>
          </nav>
        </div>

        <!-- Tab Contents -->
        <div v-show="activeTab === 'files'">
          <!-- Upsales Integration -->
          <div class="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border-2 border-blue-200 mb-6">
            <PipelineUpsalesDataFetcher
              :appointments-count="appointments.length"
              :opportunities-count="upsalesOpportunitiesCount"
              :last-fetched-at="upsalesLastFetchedAt"
              @data-fetched="handleUpsalesDataFetched"
            />
          </div>
        </div>
        <div v-show="activeTab === 'register'">
          <PipelineFileRegister :uploads="historicalUploads" :won-deals-uploads="wonDealsUploads" @delete-upload="handleDeleteUpload" @delete-wondeals="handleDeleteWonDeals" />
        </div>
        <div v-show="activeTab === 'personal'">
          <PipelinePersonalDashboard
            v-if="selectedPipelineUpload && selectedWonDealsUpload"
            :pipeline-data="filteredPipelineData"
            :won-deals-data="filteredWonDealsData"
            :appointments="appointments"
          />
          <div v-else class="text-center py-12 text-gray-500">Välj en pipeline-fil och en vunna affärer-fil för att visa Säljtavla</div>
        </div>
        <div v-show="activeTab === 'meetings'">
          <PipelineMeetings :appointments="appointments" />
        </div>
        <div v-show="activeTab === 'wondeals'">
          <PipelineWonDeals
            v-if="selectedWonDealsUpload"
            :won-deals="filteredWonDealsData"
            :all-deals="selectedWonDealsUpload.data"
          />
          <div v-else class="text-center py-12 text-gray-500">Välj en vunna affärer-fil under Filer-fliken</div>
        </div>
        <div v-show="activeTab === 'home'">
          <PipelineHome v-if="selectedPipelineUpload" :data="filteredPipelineData" />
          <div v-else class="text-center py-12 text-gray-500">Välj en pipeline-fil under Filer-fliken</div>
        </div>
        <div v-show="activeTab === 'forecast'">
          <PipelineForecast v-if="selectedPipelineUpload" :data="filteredPipelineData" :won-deals-data="filteredWonDealsData" />
          <div v-else class="text-center py-12 text-gray-500">Välj en pipeline-fil under Filer-fliken</div>
        </div>
        <div v-show="activeTab === 'quality'">
          <PipelineDataQuality v-if="selectedPipelineUpload" :data="filteredPipelineData" />
          <div v-else class="text-center py-12 text-gray-500">Välj en pipeline-fil under Filer-fliken</div>
        </div>
        <div v-show="activeTab === 'table'">
          <PipelineDataTable v-if="selectedUpload" :data="selectedUpload.data" :file-name="selectedUpload.fileName" :upload-date="selectedUpload.uploadDate" @clear-selection="selectedUpload = null" />
          <PipelineDataTable v-else-if="selectedPipelineUpload" :data="filteredPipelineData" />
          <div v-else class="text-center py-12 text-gray-500">Välj en pipeline-fil under Filer-fliken</div>
        </div>
        <div v-show="activeTab === 'history'">
          <PipelineHistory :uploads="historicalUploads" @delete-upload="handleDeleteUpload" @view-upload="handleViewUpload" />
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import XLSX from "xlsx";
import PipelineHome from "./pipeline/PipelineHome.vue";
import PipelineForecast from "./pipeline/PipelineForecast.vue";
import PipelineFileRegister from "./pipeline/PipelineFileRegister.vue";
import PipelineDataTable from "./pipeline/PipelineDataTable.vue";
import PipelineDataQuality from "./pipeline/PipelineDataQuality.vue";
import PipelineHistory from "./pipeline/PipelineHistory.vue";
import PipelineWonDeals from "./pipeline/PipelineWonDeals.vue";
import PipelineUpsalesDataFetcher from "./pipeline/PipelineUpsalesDataFetcher.vue";
import PipelinePersonalDashboard from "./pipeline/PipelinePersonalDashboard.vue";
import PipelineMeetings from "./pipeline/PipelineMeetings.vue";

const API_BASE = `${process.env.VUE_APP_SERVER_URL}/sales/pipeline`;

export default {
  name: "PipelineDashboard",
  title() {
    return "Pipeline Dashboard | SecurCloud";
  },
  components: {
    PipelineHome,
    PipelineForecast,
    PipelineFileRegister,
    PipelineDataTable,
    PipelineDataQuality,
    PipelineHistory,
    PipelineWonDeals,
    PipelineUpsalesDataFetcher,
    PipelinePersonalDashboard,
    PipelineMeetings,
  },
  data() {
    return {
      historicalUploads: [],
      wonDealsUploads: [],
      appointmentsUploads: [],
      selectedPipelineId: null,
      selectedWonDealsId: null,
      selectedAppointmentsId: null,
      selectedUpload: null,
      excludeCalleBaumgarten: false,
      activeTab: "files",
      tabs: [
        { id: "files", label: "Datakälla", icon: "database" },
        { id: "personal", label: "Säljtavla", icon: "user" },
        { id: "wondeals", label: "Vunna affärer", icon: "trophy" },
        { id: "meetings", label: "Möten", icon: "calendar-alt" },
        { id: "home", label: "Pipeline", icon: "filter" },
        { id: "forecast", label: "Sales Forecast", icon: "chart-line" },
        { id: "quality", label: "Data Quality", icon: "exclamation-triangle" },
        { id: "register", label: "Register", icon: "folder-open" },
        { id: "table", label: "Data", icon: "table" },
        { id: "history", label: "Historik", icon: "history" },
      ],
    };
  },
  computed: {
    selectedPipelineUpload() {
      if (!this.selectedPipelineId) return null;
      return this.historicalUploads.find((u) => u.id === this.selectedPipelineId) || null;
    },
    selectedWonDealsUpload() {
      if (!this.selectedWonDealsId) return null;
      return this.wonDealsUploads.find((u) => u.id === this.selectedWonDealsId) || null;
    },
    selectedAppointmentsUpload() {
      if (!this.selectedAppointmentsId) return null;
      return this.appointmentsUploads.find((u) => u.id === this.selectedAppointmentsId) || null;
    },
    appointments() {
      return this.selectedAppointmentsUpload ? this.selectedAppointmentsUpload.data : [];
    },
    upsalesOpportunitiesCount() {
      const pipeline = this.selectedPipelineUpload ? this.selectedPipelineUpload.data.length : 0;
      const won = this.selectedWonDealsUpload ? this.selectedWonDealsUpload.data.length : 0;
      return pipeline + won;
    },
    upsalesLastFetchedAt() {
      const dates = [
        this.selectedPipelineUpload?.uploadDate,
        this.selectedWonDealsUpload?.uploadDate,
        this.selectedAppointmentsUpload?.uploadDate,
      ].filter(Boolean).map((d) => new Date(d).getTime());
      if (dates.length === 0) return null;
      return new Date(Math.max(...dates));
    },
    filteredPipelineData() {
      if (!this.selectedPipelineUpload) return [];
      const data = this.selectedPipelineUpload.data;
      if (!this.excludeCalleBaumgarten) return data;
      return data.filter(row => row["Säljare"] !== "Calle Baumgarten");
    },
    filteredWonDealsData() {
      if (!this.selectedWonDealsUpload) return [];
      const data = this.selectedWonDealsUpload.data;
      if (!this.excludeCalleBaumgarten) return data;
      return data.filter(row => row["Säljare"] !== "Calle Baumgarten");
    },
  },
  created() {
    this.setPageTitle("Pipeline Dashboard", "pipeline_dashboard");
    this.loadHistoricalData();
    this.loadWonDealsData();
    this.loadAppointmentsData();
    this.loadCalleFilterSetting();
  },
  watch: {
    excludeCalleBaumgarten(val) {
      try {
        localStorage.setItem("exclude_calle_baumgarten", JSON.stringify(val));
      } catch (e) {
        // ignore
      }
    },
  },
  methods: {
    loadCalleFilterSetting() {
      try {
        const stored = localStorage.getItem("exclude_calle_baumgarten");
        if (stored) {
          this.excludeCalleBaumgarten = JSON.parse(stored);
        }
      } catch (e) {
        // ignore
      }
    },
    async loadHistoricalData() {
      try {
        const response = await this.axios.get(`${API_BASE}/uploads`);
        if (response.data && response.data.success) {
          this.historicalUploads = response.data.data.map((u) => ({
            id: u._id,
            fileName: u.fileName,
            uploadDate: u.uploadDate || u.createdAt,
            rowCount: u.rowCount,
            data: u.data,
          }));
          // Auto-select latest
          if (this.historicalUploads.length > 0 && !this.selectedPipelineId) {
            const latest = [...this.historicalUploads].sort(
              (a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
            )[0];
            this.selectedPipelineId = latest.id;
          }
        }
      } catch (error) {
        console.error("Error loading historical data:", error);
      }
    },
    async loadWonDealsData() {
      try {
        const response = await this.axios.get(`${API_BASE}/wondeals`);
        if (response.data && response.data.success) {
          this.wonDealsUploads = response.data.data.map((u) => ({
            id: u._id,
            fileName: u.fileName,
            uploadDate: u.uploadDate || u.createdAt,
            rowCount: u.rowCount,
            data: u.data,
          }));
          // Auto-select latest
          if (this.wonDealsUploads.length > 0 && !this.selectedWonDealsId) {
            const latest = [...this.wonDealsUploads].sort(
              (a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
            )[0];
            this.selectedWonDealsId = latest.id;
          }
        }
      } catch (error) {
        console.error("Error loading won deals data:", error);
      }
    },
    async loadAppointmentsData() {
      try {
        const response = await this.axios.get(`${API_BASE}/appointments`);
        if (response.data && response.data.success) {
          this.appointmentsUploads = response.data.data.map((u) => ({
            id: u._id,
            fileName: u.fileName,
            uploadDate: u.uploadDate || u.createdAt,
            rowCount: u.rowCount,
            data: u.data,
          }));
          if (this.appointmentsUploads.length > 0 && !this.selectedAppointmentsId) {
            const latest = [...this.appointmentsUploads].sort(
              (a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
            )[0];
            this.selectedAppointmentsId = latest.id;
          }
        }
      } catch (error) {
        console.error("Error loading appointments data:", error);
      }
    },
    async handleUpsalesDataFetched({ opportunities, appointments }) {
      try {
        const wonDealsRaw = opportunities.filter((o) => o.Fas === "Vunnen");
        const pipelineRaw = opportunities.filter((o) => o.Fas !== "Vunnen" && o.Fas !== "Avslutad - ingen order");

        // Map won deals to the won deals schema (uses OrderID instead of Opportunity ID)
        const wonDealsMapped = wonDealsRaw.map((o) => ({
          "Säljare": o["Säljare"],
          "Avslutsdatum": o["Avslutsdatum"],
          "Kontaktperson": o["Kontaktperson"],
          "Företag": o["Företag"],
          "OrderID": o["Opportunity ID"],
          "Beskrivning": o["Beskrivning"],
          "Antal": o["Antal produkter"],
          "Ordervärde": o["Ordervärde"],
          "Fas": o["Fas"],
          "Skapad datum": o["Skapad datum"],
        }));

        const stamp = new Date().toISOString().slice(0, 16).replace("T", " ");

        // Persist pipeline upload
        const pipelineRes = await this.axios.post(`${API_BASE}/uploads`, {
          fileName: `Upsales API ${stamp}`.trim(),
          rowCount: pipelineRaw.length,
          data: pipelineRaw,
        });
        if (pipelineRes.data?.success) {
          const saved = pipelineRes.data.data;
          this.historicalUploads.push({
            id: saved._id,
            fileName: saved.fileName,
            uploadDate: saved.uploadDate || saved.createdAt,
            rowCount: saved.rowCount,
            data: saved.data,
          });
          this.selectedPipelineId = saved._id;
        }

        // Persist won deals upload
        const wonRes = await this.axios.post(`${API_BASE}/wondeals`, {
          fileName: `Upsales API ${stamp} Vunna`.trim(),
          rowCount: wonDealsMapped.length,
          data: wonDealsMapped,
        });
        if (wonRes.data?.success) {
          const saved = wonRes.data.data;
          this.wonDealsUploads.push({
            id: saved._id,
            fileName: saved.fileName,
            uploadDate: saved.uploadDate || saved.createdAt,
            rowCount: saved.rowCount,
            data: saved.data,
          });
          this.selectedWonDealsId = saved._id;
        }

        // Persist appointments
        if (Array.isArray(appointments) && appointments.length > 0) {
          const apptRes = await this.axios.post(`${API_BASE}/appointments`, {
            fileName: `Upsales API ${stamp} Möten`.trim(),
            rowCount: appointments.length,
            data: appointments,
            source: "upsales",
          });
          if (apptRes.data?.success) {
            const saved = apptRes.data.data;
            this.appointmentsUploads.push({
              id: saved._id,
              fileName: saved.fileName,
              uploadDate: saved.uploadDate || saved.createdAt,
              rowCount: saved.rowCount,
              data: saved.data,
            });
            this.selectedAppointmentsId = saved._id;
          }
        }
      } catch (error) {
        console.error("Error persisting Upsales data:", error);
        this.$toasted.error("Kunde inte spara Upsales-data till databasen", { duration: 4000 });
      }
    },
    async handleDeleteUpload(id) {
      try {
        await this.axios.delete(`${API_BASE}/uploads/${id}`);
        this.historicalUploads = this.historicalUploads.filter((u) => u.id !== id);
        if (this.selectedPipelineId === id) {
          this.selectedPipelineId = this.historicalUploads.length > 0
            ? [...this.historicalUploads].sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())[0].id
            : null;
        }
        this.$toasted.success("Uppladdning borttagen", { duration: 3000 });
      } catch (error) {
        console.error("Error deleting upload:", error);
        this.$toasted.error("Kunde inte ta bort uppladdningen", { duration: 3000 });
      }
    },
    async handleDeleteWonDeals(id) {
      try {
        await this.axios.delete(`${API_BASE}/wondeals/${id}`);
        this.wonDealsUploads = this.wonDealsUploads.filter((u) => u.id !== id);
        if (this.selectedWonDealsId === id) {
          this.selectedWonDealsId = this.wonDealsUploads.length > 0
            ? [...this.wonDealsUploads].sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())[0].id
            : null;
        }
        this.$toasted.success("Uppladdning borttagen", { duration: 3000 });
      } catch (error) {
        console.error("Error deleting won deals upload:", error);
        this.$toasted.error("Kunde inte ta bort uppladdningen", { duration: 3000 });
      }
    },
    handleViewUpload(upload) {
      this.selectedUpload = upload;
      this.activeTab = "table";
    },
    handleExportPipeline() {
      if (!this.selectedPipelineUpload) return;
      const worksheet = XLSX.utils.json_to_sheet(this.selectedPipelineUpload.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
      const timestamp = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `pipeline_export_${timestamp}.xlsx`);
      this.$toasted.success("Pipeline data exporterad!", { duration: 3000 });
    },
    handleExportWonDeals() {
      if (!this.selectedWonDealsUpload) return;
      const worksheet = XLSX.utils.json_to_sheet(this.selectedWonDealsUpload.data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Won Deals");
      const timestamp = new Date().toISOString().split("T")[0];
      XLSX.writeFile(workbook, `won_deals_export_${timestamp}.xlsx`);
      this.$toasted.success("Vunna affärer exporterad!", { duration: 3000 });
    },
  },
};
</script>

<style scoped>
</style>
